'use client';

import { Suspense } from 'react';
import { usePathname } from 'next/navigation';
import { ProtectedRoute } from '@/components/auth/protected-route';
import { HeaderServer } from '@/components/layout';
import { DashboardSidebar } from './dashboard-sidebar';
import { GmailAccountProvider } from '@/contexts/gmail-account-context';
import { HelpProvider } from '@/contexts/help-context';
import { HelpDrawer } from '@/components/help';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useSubscriptionCheck } from '@/hooks/use-subscription-check';
import { useUnhandledRejectionHandler } from '@/hooks/use-unhandled-rejection-handler';
import { useSessionRefresh } from '@/hooks/use-session-refresh';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth-context';

/**
 * Client component for dashboard layout interactive parts.
 * Handles auth, navigation, and subscription checks.
 * The static layout structure is pre-rendered by DashboardLayoutServer.
 */
export function DashboardLayoutClient({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { toast } = useToast();
  const { user } = useAuth();
  const isBillingFlow = pathname?.startsWith('/onboarding') || pathname?.startsWith('/checkout');

  // Proactively refresh session when user returns after being idle.
  // This prevents "Invalid data" / 401 errors from stale tokens when the tab has been
  // in the background. Must be registered BEFORE visibility handlers that make API calls.
  useSessionRefresh(!!user);

  // Run subscription gate for every dashboard page except billing-related routes
  useSubscriptionCheck({
    enabled: !isBillingFlow,
  });

  // Handle unhandled promise rejections (e.g., "Invalid data" errors from expired sessions)
  // This catches errors that escape normal error handling when users return from AFK
  useUnhandledRejectionHandler({ toast });

  return (
    <Suspense
      fallback={
        <LoadingSpinner
          message="Loading..."
          className="text-muted-foreground"
          spinnerClassName="border-current"
        />
      }
    >
      <ProtectedRoute>
        <GmailAccountProvider>
          <HelpProvider>
            {isBillingFlow ? (
              <>
                <HeaderServer />
                <main id="main-content">{children}</main>
              </>
            ) : (
              // Linear-style shell: persistent left rail (md+) beside a slim top bar + content.
              <div className="flex min-h-screen">
                <DashboardSidebar />
                <div className="flex min-w-0 flex-1 flex-col">
                  <HeaderServer />
                  <main id="main-content" className="container pt-4 pb-24 sm:py-6 px-4 sm:px-6">
                    {children}
                  </main>
                </div>
              </div>
            )}
            <HelpDrawer />
          </HelpProvider>
        </GmailAccountProvider>
      </ProtectedRoute>
    </Suspense>
  );
}
